import React, { useState } from "react";
import { Box, Button, IconButton, Link, TextField } from "@mui/material";
import { VisibilityOutlined, VisibilityOffOutlined } from "@mui/icons-material";
import { trackEvent } from "@sbd-ctg/user-behavior-tracking";
import { passwordLogin } from "./ssoApi";
import { setForgotPasswordUsername } from "../forgotPassword/forgotPasswordSession";
import { isPasswordResetRequiredError } from "../common/utils";

export default function PasswordSignIn({
  username,
  resolved = null,
  onLoginSuccess,
  setIsForgotPassword,
  autoFocus = false,
  onPasswordResetRequired,
}) {
  const [password, setPassword] = useState("");
  const [breakGlass, setBreakGlass] = useState("");
  const [masked, setMasked] = useState(true);
  const [showBreakGlass, setShowBreakGlass] = useState(false);
  const [error, setError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [busy, setBusy] = useState(false);

  const breakGlassRequired = Boolean(
    resolved?.enforce_sso &&
      resolved?.ssoSignInEnabled &&
      resolved?.passwordSignInEnabled
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(false);

    if (!password) {
      setPasswordError(true);
      return;
    }

    setBusy(true);
    try {
      const loginId = username.includes("@")
        ? username.trim().toLowerCase()
        : username.trim();
      const token = await passwordLogin(
        loginId,
        password,
        breakGlassRequired ? breakGlass : undefined
      );
      await onLoginSuccess(token);
    } catch (err) {
      const needsReset = isPasswordResetRequiredError(err);
      trackEvent("Web Login Failure", {
        event_name: "Web Login Failure",
        submitted_user_name: username ?? "",
        status: "Failure",
        description: needsReset
          ? "Password reset required"
          : "Incorrect username/password combination.",
      });
      if (needsReset) {
        setForgotPasswordUsername(username.trim());
        onPasswordResetRequired?.(username.trim());
        return;
      }
      setError(true);
      if (err?.status === 403) {
        setPasswordError(false);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const errorHelperText = () => {
    if (error) {
      return "Incorrect username/password combination";
    }
    return "";
  };

  const passwordHelperText = () => {
    if (passwordError) {
      return "Please enter a password";
    }
    return errorHelperText();
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <TextField
        fullWidth
        margin="normal"
        label="Password"
        variant="outlined"
        type={masked ? "password" : "text"}
        value={password}
        onChange={(e) => {
          setPassword(e.target.value);
          if (e.target.value) {
            setPasswordError(false);
            setError(false);
          }
        }}
        error={passwordError || error}
        helperText={passwordHelperText()}
        onKeyDown={handleKeyDown}
        autoComplete="current-password"
        autoFocus={autoFocus}
        slotProps={{
          input: {
            name: "password",
            "data-testid": "password-input",
            endAdornment: (
              <IconButton onClick={() => setMasked(!masked)} edge="end">
                {masked ? (
                  <VisibilityOutlined className="visibility-icon" />
                ) : (
                  <VisibilityOffOutlined className="visibility-icon" />
                )}
              </IconButton>
            ),
          },
        }}
        InputLabelProps={{ shrink: true }}
      />

      {breakGlassRequired && (
        <TextField
          fullWidth
          margin="normal"
          label="Emergency access token"
          variant="outlined"
          type={showBreakGlass ? "text" : "password"}
          value={breakGlass}
          onChange={(e) => setBreakGlass(e.target.value)}
          placeholder="Required when your organization enforces SSO"
          autoComplete="off"
          slotProps={{
            input: {
              name: "break_glass",
              "data-testid": "break-glass-input",
              endAdornment: (
                <IconButton
                  onClick={() => setShowBreakGlass(!showBreakGlass)}
                  edge="end"
                  aria-label="toggle break-glass visibility"
                >
                  {showBreakGlass ? (
                    <VisibilityOffOutlined className="visibility-icon" />
                  ) : (
                    <VisibilityOutlined className="visibility-icon" />
                  )}
                </IconButton>
              ),
            },
          }}
          InputLabelProps={{ shrink: true }}
        />
      )}

      {setIsForgotPassword && (
        <Box
          mt={2}
          mr={1.3}
          textAlign="right"
          data-testid="forgot-password-box"
        >
          <Link
            component="button"
            type="button"
            color="primary"
            className="forgot-password-btn"
            onClick={() => {
              setForgotPasswordUsername(username);
              setIsForgotPassword(true);
            }}
            data-testid="forgot-password-link"
          >
            Forgot Password?
          </Link>
        </Box>
      )}

      <Button
        type="submit"
        variant="contained"
        color="primary"
        fullWidth
        className="button"
        disabled={busy}
        sx={{ mt: 5 }}
        data-testid="login-button"
      >
        {busy ? "Signing in…" : "Log in"}
      </Button>
    </Box>
  );
}
